
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";
import { normalizePropertyInfo, writePropertyInfo } from "../utils/propertyAccess";

const EMPTY_FORM = {
    name: "",
    email: "",
    phone: "",
    primaryOwnerAlternativeMobile: "",
    primaryOwnerWhatsapp: "",
    block: "",
    flatNumber: "",
    bhkType: "",
    squareFeet: "",
    dateOfPropertyPurchase: "",
    dateOfOccupancy: "",
    corpusFundAmount: "",
    corpusFundPaidDate: "",
    monthlyMaintenanceAmount: "",
    propertyType: "Single Owner",
    propertyNature: "Self-occupied",
    jointOwnerName: "",
    jointOwnerRelationship: "",
    jointOwnerEmail: "",
    jointOwnerMobile: "",
    jointOwnerWhatsapp: "",
    letOutPortion: "",
    typeOfTenancy: "",
    leasePeriod: "",
    tenantName: "",
    tenancyStartingDate: "",
    advanceAmount: "",
    monthlyRent: "",
    monthlyMaintenanceChargesBy: ""
};

const BLOCKS = ["A", "B", "C", "D", "E"];
const BHK_TYPES = ["1 BHK", "2 BHK", "3 BHK", "4 BHK"];
const RELATIONSHIPS = [
    { value: "father", label: "Father" },
    { value: "mother", label: "Mother" },
    { value: "brother", label: "Brother" },
    { value: "sister", label: "Sister" },
    { value: "spouse", label: "Spouse" },
    { value: "son", label: "Son" },
    { value: "daughter", label: "Daughter" }
    
   
];

const isInviteEmail = (value) => /^[^\s@]+@[^\s@]+\.(com|in)$/i.test(String(value || "").trim());

const storedAccountFields = () => {
    try {
        const amount = localStorage.getItem("monthlyMaintenanceAmount");
        return {
            name: String(localStorage.getItem("name") || ""),
            email: String(localStorage.getItem("email") || ""),
            monthlyMaintenanceAmount: amount == null ? "" : String(amount)
        };
    } catch {
        return { name: "", email: "", monthlyMaintenanceAmount: "" };
    }
};

const initialForm = () => ({ ...EMPTY_FORM, ...storedAccountFields() });

const SectionTitle = ({ children, first }) => (
    <div
        className="text-muted text-uppercase"
        style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            marginTop: first ? 0 : 8,
            marginBottom: 8
        }}
    >
        {children}
    </div>
);

const PropertyDetailsAdd = () => {
    const navigate = useNavigate();
    const API_BASE_URL = process.env.REACT_APP_API_URL;
    const userid = useMemo(() => {
        try {
            return String(localStorage.getItem("userid") || "").trim();
        } catch {
            return "";
        }
    }, []);

    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const isJointOwner = form.propertyType === "Joint Owner";
    const isLetOut = form.propertyNature === "Let-out";
    const isForLease = form.typeOfTenancy === "For Lease";

    const onChange = (key) => (e) => {
        const value = e?.target?.value ?? "";
        setForm((current) => ({ ...current, [key]: value }));
        setErrors((current) => {
            if (!current[key]) return current;
            const next = { ...current };
            delete next[key];
            return next;
        });
    };

    const control = (key) => `form-control${errors[key] ? " is-invalid" : ""}`;

    const fieldError = (key) =>
        errors[key] ? <div className="field-error">{errors[key]}</div> : null;

    const collectErrors = () => {
        const next = {};
        const text = (key, value, message) => {
            if (!String(value ?? "").trim()) next[key] = message;
        };
        const number = (key, value, message, { required = true, positive = false } = {}) => {
            if (value === "" || value == null) {
                if (required) next[key] = message;
                return;
            }
            const num = Number(value);
            if (!Number.isFinite(num) || num < 0 || (positive && num <= 0)) next[key] = message;
        };
        const email = (key, value, required) => {
            const trimmed = String(value || "").trim();
            if (!trimmed) {
                if (required) next[key] = "Email is required";
                return;
            }
            if (!isInviteEmail(trimmed)) {
                next[key] = "Enter a valid email with @ and ending in .com or .in";
            }
        };

        text("name", form.name, "Primary owner name is required");
        email("email", form.email, true);
        text("phone", form.phone, "Primary owner's mobile number is required");
        text("block", form.block, "Block is required");
        text("flatNumber", form.flatNumber, "Flat number is required");
        text("bhkType", form.bhkType, "BHK type is required");
        number("squareFeet", form.squareFeet, "Enter a valid built up area", { positive: true });
        text("dateOfPropertyPurchase", form.dateOfPropertyPurchase, "Property purchase date is required");
        number("corpusFundAmount", form.corpusFundAmount, "Corpus fund amount is required");
        number("monthlyMaintenanceAmount", form.monthlyMaintenanceAmount, "Monthly maintenance amount is required");

        if (isJointOwner) {
            text("jointOwnerName", form.jointOwnerName, "Joint owner name is required");
            text("jointOwnerRelationship", form.jointOwnerRelationship, "Relationship with primary owner is required");
            email("jointOwnerEmail", form.jointOwnerEmail, false);
            text("jointOwnerMobile", form.jointOwnerMobile, "Joint owner's mobile number is required");
            text("jointOwnerWhatsapp", form.jointOwnerWhatsapp, "Joint owner's WhatsApp number is required");
        }

        if (isLetOut) {
            text("letOutPortion", form.letOutPortion, "Let out portion is required");
            text("typeOfTenancy", form.typeOfTenancy, "Type of tenancy is required");
            if (isForLease) text("leasePeriod", form.leasePeriod, "Lease period is required");
            text("tenantName", form.tenantName, "Tenant name is required");
            text("tenancyStartingDate", form.tenancyStartingDate, "Tenancy starting date is required");
            number("advanceAmount", form.advanceAmount, "Enter a valid advance or lease amount", { required: false });
            number("monthlyRent", form.monthlyRent, "Enter a valid monthly rent", { required: false });
            text("monthlyMaintenanceChargesBy", form.monthlyMaintenanceChargesBy, "Monthly maintenance charges is required");
        }

        return next;
    };

    const buildPayload = () => {
        const payload = {
            action: "admin.createTenant",
            userid,
            name: form.name.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
            block: form.block,
            flatNumber: form.flatNumber.trim(),
            bhkType: form.bhkType,
            squareFeet: Number(form.squareFeet),
            dateOfPropertyPurchase: form.dateOfPropertyPurchase,
            corpusFundAmount: Number(form.corpusFundAmount),
            monthlyMaintenanceAmount: Number(form.monthlyMaintenanceAmount),
            propertyType: form.propertyType,
            propertyNature: form.propertyNature
        };

        if (form.primaryOwnerAlternativeMobile.trim()) {
            payload.primaryOwnerAlternativeMobile = form.primaryOwnerAlternativeMobile.trim();
        }
        if (form.primaryOwnerWhatsapp.trim()) {
            payload.primaryOwnerWhatsapp = form.primaryOwnerWhatsapp.trim();
        }
        if (form.dateOfOccupancy) payload.dateOfOccupancy = form.dateOfOccupancy;
        if (form.corpusFundPaidDate) payload.corpusFundPaidDate = form.corpusFundPaidDate;

        if (isJointOwner) {
            payload.jointOwnerName = form.jointOwnerName.trim();
            payload.jointOwnerRelationship = form.jointOwnerRelationship;
            if (form.jointOwnerEmail.trim()) payload.jointOwnerEmail = form.jointOwnerEmail.trim();
            payload.jointOwnerMobile = form.jointOwnerMobile.trim();
            payload.jointOwnerWhatsapp = form.jointOwnerWhatsapp.trim();
        }

        if (isLetOut) {
            payload.letOutPortion = form.letOutPortion;
            payload.typeOfTenancy = form.typeOfTenancy;
            if (isForLease) payload.leasePeriod = form.leasePeriod.trim();
            payload.tenantName = form.tenantName.trim();
            payload.tenancyStartingDate = form.tenancyStartingDate;
            if (form.advanceAmount !== "") payload.advanceAmount = Number(form.advanceAmount);
            if (form.monthlyRent !== "") payload.monthlyRent = Number(form.monthlyRent);
            payload.monthlyMaintenanceChargesBy = form.monthlyMaintenanceChargesBy;
        }

        return payload;
    };

    const save = async () => {
        if (!API_BASE_URL) {
            toast.error("Missing REACT_APP_API_URL in environment");
            return;
        }
        if (!userid) {
            toast.error("Missing userid. Please login again.");
            return;
        }

        setSubmitting(true);
        let didIncrement = false;
        try {
            loaderIncrement();
            didIncrement = true;
            const response = await axios.post(`${API_BASE_URL}`, buildPayload());
            const data = response?.data;
            if (data?.success === true || data?.status === "success") {
                const propertyInfo =
                    data?.propertyInfo ?? data?.data?.propertyInfo ?? data?.property?.propertyInfo;
                const nextInfo =
                    propertyInfo && normalizePropertyInfo(propertyInfo) !== "pending"
                        ? propertyInfo
                        : "waitingforApproval";
                writePropertyInfo(nextInfo);
                toast.success(data?.message || "Owner created successfully");
                navigate("/property-details");
            } else {
                toast.error(data?.message || "Failed to save property details");
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || "Something went wrong");
        } finally {
            if (didIncrement) loaderDecrement();
            setSubmitting(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const next = collectErrors();
        setErrors(next);
        if (Object.keys(next).length) {
            window.setTimeout(() => {
                document.querySelector(".employee-form .is-invalid")?.scrollIntoView({
                    behavior: "smooth",
                    block: "center"
                });
            }, 0);
            return;
        }
        save();
    };

    return (
        <div className="employee-form">
            <div className="content-header">
                <div className="container-fluid">
                    <h1 className="m-0 txt_lft">Property details</h1>
                    <p style={{ margin: 0, color: "#6c757d", fontSize: 14 }}>
                        Add the primary owner and property. Joint owner and let-out fields appear when those options are selected.
                    </p>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="card">
                        <div className="card-body">
                            <form onSubmit={handleSubmit}>
                                <>
                                    <SectionTitle first>Primary owner</SectionTitle>
                                    <div className="row invite-form-row">
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <div className="form-group">
                                                <label className="form-label">
                                                    Primary Owner Name <span className="required-star">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={control("name")}
                                                    placeholder="Enter primary owner name"
                                                    value={form.name}
                                                    onChange={onChange("name")}
                                                />
                                                {fieldError("name")}
                                            </div>
                                        </div>
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <div className="form-group">
                                                <label className="form-label">
                                                    Email <span className="required-star">*</span>
                                                </label>
                                                <input
                                                    type="email"
                                                    className={control("email")}
                                                    placeholder="name@email.com"
                                                    value={form.email}
                                                    readOnly
                                                />
                                                {fieldError("email")}

                                            </div>
                                        </div>
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <div className="form-group">
                                                <label className="form-label">
                                                    Primary Owner’s Mobile No. <span className="required-star">*</span>
                                                </label>
                                                <input
                                                    type="tel"
                                                    className={control("phone")}
                                                    placeholder="Enter mobile number"
                                                    value={form.phone}
                                                    onChange={onChange("phone")}
                                                />
                                                {fieldError("phone")}
                                            </div>
                                        </div>
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <div className="form-group">
                                                <label className="form-label">Primary Owner’s Alternative Mobile No.</label>
                                                <input
                                                    type="tel"
                                                    className="form-control"
                                                    placeholder="Optional"
                                                    value={form.primaryOwnerAlternativeMobile}
                                                    onChange={onChange("primaryOwnerAlternativeMobile")}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row invite-form-row">
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <div className="form-group">
                                                <label className="form-label">Primary Owner’s WhatsApp No.</label>
                                                <input
                                                    type="tel"
                                                    className="form-control"
                                                    placeholder="Optional"
                                                    value={form.primaryOwnerWhatsapp}
                                                    onChange={onChange("primaryOwnerWhatsapp")}
                                                />
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-6 col-lg-3">
                                            <div className="form-group">
                                                <label className="form-label">
                                                    Property Type <span className="required-star">*</span>
                                                </label>
                                                <select
                                                    className="form-control"
                                                    value={form.propertyType}
                                                    onChange={onChange("propertyType")}
                                                >
                                                    <option value="Single Owner">Single Owner</option>
                                                    <option value="Joint Owner">Joint Owner</option>
                                                </select>
                                            </div>
                                        </div>

                                    </div>


                                    {isJointOwner ? (
                                        <>
                                            <SectionTitle first>Joint owner</SectionTitle>
                                            <div className="row invite-form-row">
                                                <div className="col-12 col-md-6 col-lg-3">
                                                    <div className="form-group">
                                                        <label className="form-label">
                                                            Joint owner Name <span className="required-star">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className={control("jointOwnerName")}
                                                            placeholder="Enter joint owner name"
                                                            value={form.jointOwnerName}
                                                            onChange={onChange("jointOwnerName")}
                                                        />
                                                        {fieldError("jointOwnerName")}
                                                    </div>
                                                </div>
                                                <div className="col-12 col-md-6 col-lg-3">
                                                    <div className="form-group">
                                                        <label className="form-label">
                                                            Relationship with primary owner <span className="required-star">*</span>
                                                        </label>
                                                        <select
                                                            className={control("jointOwnerRelationship")}
                                                            value={form.jointOwnerRelationship}
                                                            onChange={onChange("jointOwnerRelationship")}
                                                        >
                                                            <option value="">Select relationship</option>
                                                            {RELATIONSHIPS.map((item) => (
                                                                <option key={item.value} value={item.value}>
                                                                    {item.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {fieldError("jointOwnerRelationship")}
                                                    </div>
                                                </div>
                                                <div className="col-12 col-md-6 col-lg-3">
                                                    <div className="form-group">
                                                        <label className="form-label">Joint owner’s Email ID</label>
                                                        <input
                                                            type="email"
                                                            className={control("jointOwnerEmail")}
                                                            placeholder="Optional"
                                                            value={form.jointOwnerEmail}
                                                            onChange={onChange("jointOwnerEmail")}
                                                        />
                                                        {fieldError("jointOwnerEmail")}
                                                    </div>
                                                </div>
                                                <div className="col-12 col-md-6 col-lg-3">
                                                    <div className="form-group">
                                                        <label className="form-label">
                                                            Joint owner’s Mobile No. <span className="required-star">*</span>
                                                        </label>
                                                        <input
                                                            type="tel"
                                                            className={control("jointOwnerMobile")}
                                                            placeholder="Enter mobile number"
                                                            value={form.jointOwnerMobile}
                                                            onChange={onChange("jointOwnerMobile")}
                                                        />
                                                        {fieldError("jointOwnerMobile")}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row invite-form-row">
                                                <div className="col-12 col-md-6 col-lg-3">
                                                    <div className="form-group">
                                                        <label className="form-label">
                                                            Joint owner’s WhatsApp No. <span className="required-star">*</span>
                                                        </label>
                                                        <input
                                                            type="tel"
                                                            className={control("jointOwnerWhatsapp")}
                                                            placeholder="Enter WhatsApp number"
                                                            value={form.jointOwnerWhatsapp}
                                                            onChange={onChange("jointOwnerWhatsapp")}
                                                        />
                                                        {fieldError("jointOwnerWhatsapp")}
                                                    </div>
                                                </div>
                                            </div>
                                        </>
                                    ) : null}


                                    <SectionTitle>Property</SectionTitle>
                                    <div className="row invite-form-row">
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <div className="form-group">
                                                <label className="form-label">
                                                    Block <span className="required-star">*</span>
                                                </label>
                                                <select className={control("block")} value={form.block} onChange={onChange("block")}>
                                                    <option value="">Select block</option>
                                                    {BLOCKS.map((block) => (
                                                        <option key={block} value={block}>
                                                            {block}
                                                        </option>
                                                    ))}
                                                </select>
                                                {fieldError("block")}
                                            </div>
                                        </div>
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <div className="form-group">
                                                <label className="form-label">
                                                    Flat Number <span className="required-star">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={control("flatNumber")}
                                                    placeholder="Enter flat number"
                                                    value={form.flatNumber}
                                                    onChange={onChange("flatNumber")}
                                                />
                                                {fieldError("flatNumber")}
                                            </div>
                                        </div>
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <div className="form-group">
                                                <label className="form-label">
                                                    BHK type <span className="required-star">*</span>
                                                </label>
                                                <select className={control("bhkType")} value={form.bhkType} onChange={onChange("bhkType")}>
                                                    <option value="">Select BHK</option>
                                                    {BHK_TYPES.map((type) => (
                                                        <option key={type} value={type}>
                                                            {type}
                                                        </option>
                                                    ))}
                                                </select>
                                                {fieldError("bhkType")}
                                            </div>
                                        </div>
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <div className="form-group">
                                                <label className="form-label">
                                                    Sq.ft (Built up area) <span className="required-star">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className={control("squareFeet")}
                                                    placeholder="Enter built up area"
                                                    value={form.squareFeet}
                                                    onChange={onChange("squareFeet")}
                                                />
                                                {fieldError("squareFeet")}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row invite-form-row">
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <div className="form-group">
                                                <label className="form-label">
                                                    Property Purchase Date <span className="required-star">*</span>
                                                </label>
                                                <input
                                                    type="date"
                                                    className={control("dateOfPropertyPurchase")}
                                                    value={form.dateOfPropertyPurchase}
                                                    onChange={onChange("dateOfPropertyPurchase")}
                                                />
                                                {fieldError("dateOfPropertyPurchase")}
                                            </div>
                                        </div>
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <div className="form-group">
                                                <label className="form-label">Date of Occupancy</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={form.dateOfOccupancy}
                                                    onChange={onChange("dateOfOccupancy")}
                                                />
                                            </div>
                                        </div>
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <div className="form-group">
                                                <label className="form-label">
                                                    Corpus Fund Amount <span className="required-star">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    step="1"
                                                    className={control("corpusFundAmount")}
                                                    placeholder="Enter corpus fund"
                                                    value={form.corpusFundAmount}
                                                    onChange={onChange("corpusFundAmount")}
                                                />
                                                {fieldError("corpusFundAmount")}
                                            </div>
                                        </div>
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <div className="form-group">
                                                <label className="form-label">Corpus Fund paid date</label>
                                                <input
                                                    type="date"
                                                    className="form-control"
                                                    value={form.corpusFundPaidDate}
                                                    onChange={onChange("corpusFundPaidDate")}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="row invite-form-row">
                                        <div className="col-12 col-md-6 col-lg-3">
                                            <div className="form-group">
                                                <label className="form-label">
                                                    Monthly Maintenance Amount <span className="required-star">*</span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className={control("monthlyMaintenanceAmount")}
                                                    placeholder="Enter monthly maintenance"
                                                    value={form.monthlyMaintenanceAmount}
                                                    readOnly
                                                />
                                                {fieldError("monthlyMaintenanceAmount")}
                                            </div>
                                        </div>

                                        <div className="col-12 col-md-6 col-lg-3">
                                            <div className="form-group">
                                                <label className="form-label">
                                                    Property Nature <span className="required-star">*</span>
                                                </label>
                                                <select
                                                    className="form-control"
                                                    value={form.propertyNature}
                                                    onChange={onChange("propertyNature")}
                                                >
                                                    <option value="Self-occupied">Self-occupied</option>
                                                    <option value="Let-out">Let-out</option>
                                                    <option value="Vacant">Vacant</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </>



                                {isLetOut ? (
                                    <>
                                        <SectionTitle first>Tenant details</SectionTitle>
                                        <div className="row invite-form-row">
                                            <div className="col-12 col-md-6 col-lg-3">
                                                <div className="form-group">
                                                    <label className="form-label">
                                                        Let out portion <span className="required-star">*</span>
                                                    </label>
                                                    <select
                                                        className={control("letOutPortion")}
                                                        value={form.letOutPortion}
                                                        onChange={onChange("letOutPortion")}
                                                    >
                                                        <option value="">Select portion</option>
                                                        <option value="Full Let-out">Full Let-out</option>
                                                        <option value="Partial Let-out">Partial Let-out</option>
                                                    </select>
                                                    {fieldError("letOutPortion")}
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6 col-lg-3">
                                                <div className="form-group">
                                                    <label className="form-label">
                                                        Type of Tenancy <span className="required-star">*</span>
                                                    </label>
                                                    <select
                                                        className={control("typeOfTenancy")}
                                                        value={form.typeOfTenancy}
                                                        onChange={onChange("typeOfTenancy")}
                                                    >
                                                        <option value="">Select tenancy</option>
                                                        <option value="For Rent">For Rent</option>
                                                        <option value="For Lease">For Lease</option>
                                                        <option value="For Relative (No Rent)">For Relative (No Rent)</option>
                                                    </select>
                                                    {fieldError("typeOfTenancy")}
                                                </div>
                                            </div>
                                            {isForLease ? (
                                                <div className="col-12 col-md-6 col-lg-3">
                                                    <div className="form-group">
                                                        <label className="form-label">
                                                            Lease Period <span className="required-star">*</span>
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className={control("leasePeriod")}
                                                            placeholder="e.g. 11 months"
                                                            value={form.leasePeriod}
                                                            onChange={onChange("leasePeriod")}
                                                        />
                                                        {fieldError("leasePeriod")}
                                                    </div>
                                                </div>
                                            ) : null}
                                            <div className="col-12 col-md-6 col-lg-3">
                                                <div className="form-group">
                                                    <label className="form-label">
                                                        Tenant Name <span className="required-star">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className={control("tenantName")}
                                                        placeholder="Enter tenant name"
                                                        value={form.tenantName}
                                                        onChange={onChange("tenantName")}
                                                    />
                                                    {fieldError("tenantName")}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row invite-form-row">
                                            <div className="col-12 col-md-6 col-lg-3">
                                                <div className="form-group">
                                                    <label className="form-label">
                                                        Tenancy Starting Date <span className="required-star">*</span>
                                                    </label>
                                                    <input
                                                        type="date"
                                                        className={control("tenancyStartingDate")}
                                                        value={form.tenancyStartingDate}
                                                        onChange={onChange("tenancyStartingDate")}
                                                    />
                                                    {fieldError("tenancyStartingDate")}
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6 col-lg-3">
                                                <div className="form-group">
                                                    <label className="form-label">Advance Amount/Lease Amount</label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="1"
                                                        className={control("advanceAmount")}
                                                        placeholder="Optional"
                                                        value={form.advanceAmount}
                                                        onChange={onChange("advanceAmount")}
                                                    />
                                                    {fieldError("advanceAmount")}
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6 col-lg-3">
                                                <div className="form-group">
                                                    <label className="form-label">Monthly Rent</label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="1"
                                                        className={control("monthlyRent")}
                                                        placeholder="Optional"
                                                        value={form.monthlyRent}
                                                        onChange={onChange("monthlyRent")}
                                                    />
                                                    {fieldError("monthlyRent")}
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-6 col-lg-3">
                                                <div className="form-group">
                                                    <label className="form-label">
                                                        Monthly Maintenance Charges <span className="required-star">*</span>
                                                    </label>
                                                    <select
                                                        className={control("monthlyMaintenanceChargesBy")}
                                                        value={form.monthlyMaintenanceChargesBy}
                                                        onChange={onChange("monthlyMaintenanceChargesBy")}
                                                    >
                                                        <option value="">Select</option>
                                                        <option value="By Owner">By Owner</option>
                                                        <option value="By Tenant">By Tenant</option>
                                                    </select>
                                                    {fieldError("monthlyMaintenanceChargesBy")}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                ) : null}

                                <div className="row invite-form-row">
                                    <div className="col-12">
                                        <div className="form-group invite-form-actions mb-0">
                                            <div className="d-flex flex-wrap justify-content-lg-end" style={{ gap: 10 }}>
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary"
                                                    disabled={submitting}
                                                    onClick={() => {
                                                        setForm(initialForm());
                                                        setErrors({});
                                                    }}
                                                >
                                                    Clear
                                                </button>
                                                <button type="submit" className="btn btn-primary" disabled={submitting}>
                                                    {submitting ? "Saving…" : "Save"}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PropertyDetailsAdd;
